import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Badge,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  ShoppingCart,
  Favorite,
  FavoriteBorder,
  Share,
  ZoomIn,
  CheckCircle,
  LocalShipping,
  Security,
  Verified,
  Business,
  Phone,
  Email,
  LocationOn,
  Schedule,
  RequestQuote,
  Home,
  Category,
  ArrowBack,
  ArrowForward,
  Close,
  Add,
  Remove,
  Info,
  BookOnline,
  PlayArrow,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

import { fetchProductById, fetchSimilarProducts } from '../store/slices/productSlice';
import { addToCart } from '../store/slices/cartSlice';
import { addToWishlist, removeFromWishlist, fetchCustomerWishlist } from '../store/customerSlice';
import { 
  fetchProductReviewsPublic,
  fetchSellerReviewAnalytics 
} from '../store/slices/reviewSlice';
import ReviewSystem from '../components/review/ReviewSystem';
import ReportProductButton from '../components/product/ReportProductButton';
import VisitRequestDialog from '../components/VisitRequestDialog';
import BookLotDialog from '../components/product/BookLotDialog';
import { getProductPrimaryImageUrl, getProductImageUrls } from '../utils/imageUtils';
import { getBestPromotionForProduct } from '../utils/promotionUtils';
import { visitRequestService } from '../services/visitRequestService';
import lotRequestService from '../services/lotRequestService';
import auditService from '../services/auditService';
import { lotBalance, calculateMinSlabsFromMoq, getMinOrderSlabs } from '../utils/lotUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { currentProduct, similarProducts, loading, error, products: allProducts } = useSelector(state => state.products);
  const { user } = useSelector(state => state.auth);
  const { wishlist } = useSelector(state => state.customer);
  const { productAnalytics, sellerAnalytics } = useSelector(state => state.reviews);
  
  // Local state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedLotIndex, setSelectedLotIndex] = useState(0);

  const handleTabChange = (event, newValue) => {
    console.log('🎯 Tab changed to:', newValue);
    setSelectedTab(newValue);
  };



  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  
  // Book a lot dialog state
  const [bookLotDialogOpen, setBookLotDialogOpen] = useState(false);

  // Check if product is in wishlist
  const isInWishlist = wishlist?.items?.some(item => item.productId === id) || false;

  // State to force re-render when promotions change
  const [promotionUpdateKey, setPromotionUpdateKey] = React.useState(0);
  
  // Get promotion for this product (re-check when promotionUpdateKey changes)
  // Calculate promotion early, before any conditional returns
  const promotion = React.useMemo(() => {
    if (!currentProduct?.product) return null;
    return getBestPromotionForProduct(currentProduct.product.id, currentProduct.product.category, allProducts || []);
  }, [currentProduct?.product?.id, currentProduct?.product?.category, allProducts, promotionUpdateKey]);
  
  // Listen for promotion changes to refresh product display
  useEffect(() => {
    const handlePromotionChange = () => {
      // Update key to force re-render
      setPromotionUpdateKey(prev => prev + 1);
    };
    
    window.addEventListener('promotionsUpdated', handlePromotionChange);
    
    return () => {
      window.removeEventListener('promotionsUpdated', handlePromotionChange);
    };
  }, []);
  
  // Fetch product data on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id]);

  // Fetch similar products when current product is loaded
  useEffect(() => {
    if (currentProduct?.product) {
      dispatch(fetchSimilarProducts({
        productId: id,
        category: currentProduct.product.category,
        size: 4
      }));
    }
  }, [dispatch, currentProduct, id]);

  // Initialize quantity from minimumOrderQuantity (SFT) when product loads.
  // Converts the SFT minimum into the actual number of slabs required.
  useEffect(() => {
    if (currentProduct?.product) {
      const p = currentProduct.product;
      const lots = p.lots && p.lots.length > 0
        ? p.lots
        : (p.lotLength || p.lotNos)
          ? [{ lotLength: p.lotLength, lotWidth: p.lotWidth, lotNos: p.lotNos }]
          : [];
      const moqSft = parseFloat(p.minimumOrderQuantity) || 0;
      if (moqSft > 0 && lots.length > 0) {
        const { minSlabs } = calculateMinSlabsFromMoq(moqSft, lots);
        setQuantity(minSlabs);
      } else if (moqSft > 0) {
        setQuantity(1);
      }
    }
  }, [currentProduct?.product?.id]);

  // Fetch wishlist if user is logged in
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchCustomerWishlist(user.id));
    }
  }, [dispatch, user?.id]);

  // Combine images and videos for thumbnail display
  const mediaItems = useMemo(() => {
    if (!currentProduct?.product) return [];
    
    const product = currentProduct.product;
    const images = getProductImageUrls(product);
    const items = [];
    
    // Add images
    images.forEach((imageUrl, index) => {
      items.push({ type: 'image', url: imageUrl, index });
    });
    
    // Add videos
    if (product.videoUrls && product.videoUrls.length > 0) {
      product.videoUrls.forEach((videoUrl, index) => {
        let fullVideoUrl = typeof videoUrl === 'string' ? videoUrl : (videoUrl.url || videoUrl.videoUrl || '');
        if (typeof fullVideoUrl === 'string' && !fullVideoUrl.startsWith('http')) {
          if (fullVideoUrl.startsWith('/api/files/')) {
            fullVideoUrl = `${API_BASE_URL.replace('/api', '')}${fullVideoUrl}`;
          } else if (fullVideoUrl.startsWith('/uploads/products/')) {
            const filename = fullVideoUrl.replace('/uploads/products/', '');
            fullVideoUrl = `${API_BASE_URL.replace('/api', '')}/api/files/images/products/${filename}`;
          } else {
            fullVideoUrl = `${API_BASE_URL.replace('/api', '')}/api/files/images/products/${fullVideoUrl}`;
          }
        }
        items.push({ type: 'video', url: fullVideoUrl, index });
      });
    }
    
    return items;
  }, [currentProduct]);

  // Fetch review analytics when product is loaded
  // Fetch reviews with minimal data (size=1) just to get analytics for display
  // This ensures rating shows immediately, not just when Reviews tab opens
  useEffect(() => {
    if (currentProduct?.product) {
      const productId = currentProduct.product.id;
      
      // Fetch reviews with minimal data to get analytics (for rating display)
      // ReviewSystem will fetch full reviews when Reviews tab opens, but we have guards to prevent duplicates
      dispatch(fetchProductReviewsPublic({ 
        productId, 
        params: { page: 0, size: 1 } // Minimal data, just to get analytics
      }));
      
      if (currentProduct.product.sellerId) {
        dispatch(fetchSellerReviewAnalytics(currentProduct.product.sellerId));
      }
      
      // Log product view
      try {
        const userRole = user?.role || 'GUEST';
        auditService.logProductView(currentProduct.product.id, userRole);
      } catch (error) {
        console.error('Failed to log product view:', error);
      }
    }
  }, [dispatch, currentProduct?.product?.id, user?.role]);


  const handleAddToCart = () => {
    if (currentProduct?.product) {
      // Compute per-piece price using lot dimensions so cart total = L×W×qty/144 × pricePerSft
      const qty = typeof quantity === 'number' ? quantity : parseInt(quantity) || 0;
      const lots = currentProduct.product.lots && currentProduct.product.lots.length > 0
        ? currentProduct.product.lots
        : (currentProduct.product.lotLength || currentProduct.product.lotNos)
          ? [{ lotLength: currentProduct.product.lotLength, lotWidth: currentProduct.product.lotWidth, lotNos: currentProduct.product.lotNos }]
          : [];
      const effectiveUnitPrice = Number(currentProduct.product.discountedPrice || currentProduct.product.price) || 0;
      let pricePerPiece = effectiveUnitPrice;
      if (lots.length > 0 && qty > 0) {
        let remaining = qty;
        let totalSftConsumed = 0;
        for (const lot of lots) {
          if (remaining <= 0) break;
          const consumed = Math.min(lotBalance(lot), remaining);
          remaining -= consumed;
          totalSftConsumed += ((parseFloat(lot.lotLength) || 0) * (parseFloat(lot.lotWidth) || 0) * consumed) / 144;
        }
        if (totalSftConsumed > 0) pricePerPiece = (totalSftConsumed * effectiveUnitPrice) / qty;
      }
      dispatch(addToCart({
        product: { ...currentProduct.product, price: pricePerPiece, discountedPrice: null, pricePerSft: effectiveUnitPrice, minOrderSlabs: getMinOrderSlabs(currentProduct.product) },
        quantity: quantity
      }));
      toast.success(`${currentProduct.product.name} added to cart!`);
    }
  };

  const handleWishlistToggle = () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/products/${id}` } } });
      return;
    }

    if (isInWishlist) {
      dispatch(removeFromWishlist({ customerId: user.id, productId: id }));
      toast.success('Removed from wishlist');
    } else {
      dispatch(addToWishlist({ 
        customerId: user.id, 
        productId: id,
        productData: currentProduct.product
      }));
      toast.success('Added to wishlist');
    }
  };

  const handleRequestVisit = () => {
    if (!user) {
      toast.error('Please login to request a visit');
      return;
    }
    setVisitDialogOpen(true);
  };

  const handleVisitSubmit = async (visitData) => {
    try {
      await visitRequestService.createVisitRequest({
        ...visitData,
        productId: id
      });
      toast.success('Visit request submitted successfully!');
      setVisitDialogOpen(false);
    } catch (error) {
      console.error('Error submitting visit request:', error);
      toast.error('Failed to submit visit request. Please try again.');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: currentProduct?.product?.name,
        text: currentProduct?.product?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Product link copied to clipboard!');
    }
  };

  // Compute max orderable quantity from lots[] (source of truth), falling back to stockQuantity
  const getMaxOrderQuantity = (prod) => {
    if (!prod) return 999;
    if (prod.lots && prod.lots.length > 0) {
      const total = prod.lots.reduce((sum, l) => sum + lotBalance(l), 0);
      if (total > 0) return total;
    }
    if (prod.lotNos != null) return parseInt(prod.lotNos) || prod.stockQuantity || 999;
    return prod.stockQuantity || 999;
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    const p = currentProduct?.product;
    const lots = p?.lots && p.lots.length > 0
      ? p.lots
      : (p?.lotLength || p?.lotNos)
        ? [{ lotLength: p.lotLength, lotWidth: p.lotWidth, lotNos: p.lotNos }]
        : [];
    const moqSft = parseFloat(p?.minimumOrderQuantity) || 0;
    const moqMinSlabs = moqSft > 0 && lots.length > 0
      ? calculateMinSlabsFromMoq(moqSft, lots).minSlabs
      : 1;
    const maxStock = getMaxOrderQuantity(p);

    if (newQuantity >= moqMinSlabs && newQuantity <= maxStock) {
      setQuantity(newQuantity);
    }
  };

  const handleQuantityInputChange = (event) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      setQuantity('');
      return;
    }

    const numericValue = parseInt(inputValue);

    if (isNaN(numericValue)) {
      return;
    }

    if (numericValue > 0) {
      setQuantity(numericValue);
    }
  };

  const handleQuantityBlur = () => {
    const p = currentProduct?.product;
    const lots = p?.lots && p.lots.length > 0
      ? p.lots
      : (p?.lotLength || p?.lotNos)
        ? [{ lotLength: p.lotLength, lotWidth: p.lotWidth, lotNos: p.lotNos }]
        : [];
    const moqSft = parseFloat(p?.minimumOrderQuantity) || 0;
    const moqMinSlabs = moqSft > 0 && lots.length > 0
      ? calculateMinSlabsFromMoq(moqSft, lots).minSlabs
      : 1;
    const maxStock = getMaxOrderQuantity(p);

    if (quantity === '' || quantity < moqMinSlabs) {
      setQuantity(moqMinSlabs);
    } else if (quantity > maxStock) {
      setQuantity(maxStock);
      toast.error(`Maximum available quantity is ${maxStock}`);
    }
  };



  const handleBookALot = () => {
    if (!user) {
      toast.error('Please login to book a lot');
      return;
    }
    setBookLotDialogOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={30} />
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" height={50} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/products')}>
          Back to Products
        </Button>
      </Container>
    );
  }

  // No product found
  if (!currentProduct?.product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Product not found
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/products')}>
          Back to Products
        </Button>
      </Container>
    );
  }

  const product = currentProduct.product;
  const images = getProductImageUrls(product);
  const hasImages = getProductPrimaryImageUrl(product) || (images && images.length > 0);
  const primaryImage = getProductPrimaryImageUrl(product) || '/mos-logo.jpg';
  // Promotion is already calculated above using useMemo

  // Derive the list of lots to display. Prefers the product.lots array (multi-lot),
  // falls back to the legacy flat lot fields as a single "Default Lot".
  const effectiveLots = (() => {
    if (product.lots && product.lots.length > 0) {
      return product.lots;
    }
    if (product.lotLength || product.lotWidth || product.lotNos || product.lotSft) {
      return [{
        lotId: 'default',
        lotName: 'Default Lot',
        lotLength: product.lotLength,
        lotWidth: product.lotWidth,
        lotNos: product.lotNos,
        lotSft: product.lotSft,
        lotLocation: product.lotLocation,
      }];
    }
    return [];
  })();

  const selectedLot = effectiveLots[selectedLotIndex] || effectiveLots[0] || null;

  // Total SFT across all lots (dimensions in inches → divide by 144)
  const totalSft = effectiveLots.reduce((sum, lot) => {
    const l = parseFloat(lot.lotLength) || 0;
    const w = parseFloat(lot.lotWidth) || 0;
    const n = parseInt(lot.lotNos) || 0;
    return sum + (l * w * n / 144);
  }, 0);

  // Shared lot location (product-level or from first lot)
  const sharedLotLocation = product.lotLocation || effectiveLots[0]?.lotLocation || '';

  // Effective unit price after discount/promotion
  const finalUnitPrice = (() => {
    const base = Number(product.price) || 0;
    let p = Number(product.discountedPrice || product.price) || 0;
    if (promotion) {
      if (promotion.discountType === 'percentage') {
        p = base * (1 - (Number(promotion.discount) || 0) / 100);
      } else if (promotion.discountType === 'amount') {
        p = Math.max(0, base - (Number(promotion.discountAmount) || 0));
      }
    }
    return p;
  })();

  // Active lot = first lot that still has stock; used for SFT calculation and booking
  const activeLotForCalc = effectiveLots.find(l => lotBalance(l) > 0) || effectiveLots[0] || null;
  console.log('[ProductDetail] activeLotForCalc=', activeLotForCalc, 'effectiveLots=', effectiveLots);

  // moqMinSlabs: minimum integer slabs derived from minimumOrderQuantity (SFT) via lot-by-lot algorithm
  const moqMinSlabs = (() => {
    const moqSft = parseFloat(product.minimumOrderQuantity) || 0;
    if (moqSft <= 0 || effectiveLots.length === 0) return 1;
    return calculateMinSlabsFromMoq(moqSft, effectiveLots).minSlabs;
  })();

  // Real-time Selected SFT — multi-lot sequential consumption
  const realTimeSft = (() => {
    const qty = typeof quantity === 'number' ? quantity : parseInt(quantity) || 0;
    if (!effectiveLots.length || qty <= 0) return 0;
    let remaining = qty;
    let sft = 0;
    for (const lot of effectiveLots) {
      if (remaining <= 0) break;
      const nos = lotBalance(lot);
      const l = parseFloat(lot.lotLength) || 0;
      const w = parseFloat(lot.lotWidth) || 0;
      if (nos <= 0 || l <= 0 || w <= 0) continue;
      const consumed = Math.min(nos, remaining);
      sft += consumed * l * w / 144;
      remaining -= consumed;
    }
    return sft;
  })();

  // ── Normal-product selected / available SFT ─────────────────────────────
  // selectedSft = SFT consumed by chosen quantity (sequential lot consumption = realTimeSft)
  // availableSft = the product's persisted inventory from the API (the source of truth);
  // falls back to totalSft only for a product not yet migrated (field missing)
  const availableSft = product.availableSft != null ? (Number(product.availableSft) || 0) : totalSft;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link component={RouterLink} to="/products" sx={{ display: 'flex', alignItems: 'center' }}>
          <Category sx={{ mr: 0.5 }} fontSize="inherit" />
          Products
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ mb: 2 }}>
              <Box sx={{ position: 'relative' }}>
                {mediaItems[selectedImageIndex]?.type === 'video' ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 450,
                      position: 'relative',
                      bgcolor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      aspectRatio: '4/3',
                    }}
                  >
                    <Box
                      component="video"
                      src={mediaItems[selectedImageIndex].url}
                      controls
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => setImageDialogOpen(true)}
                    />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    sx={{
                      width: '100%',
                      height: 450,
                      objectFit: 'cover',
                      cursor: 'zoom-in',
                      aspectRatio: '4/3',
                      filter: hasImages ? 'none' : 'grayscale(100%)',
                      opacity: hasImages ? 1 : 0.7,
                    }}
                    image={mediaItems[selectedImageIndex]?.url || images[selectedImageIndex] || primaryImage}
                    alt={product.name}
                    onClick={() => setImageDialogOpen(true)}
                    onError={(e) => {
                      e.target.src = '/mos-logo.jpg';
                      e.target.style.filter = 'grayscale(100%)';
                      e.target.style.opacity = '0.7';
                    }}
                  />
                )}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                  }}
                  onClick={() => setImageDialogOpen(true)}
                >
                  <ZoomIn />
                </IconButton>
                
                {/* Product badges */}
                <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {product.featured && (
                    <Chip label="Featured" color="primary" size="small" />
                  )}
                  {promotion && (
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                        borderRadius: '20px',
                        px: 2,
                        py: 0.75,
                        boxShadow: '0 4px 16px rgba(255, 107, 107, 0.5)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: 800,
                          fontSize: '0.8rem',
                          letterSpacing: '0.5px',
                          textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 900,
                          }}
                        >
                          {promotion.discountType === 'amount' ? '₹' : ''}
                          {promotion.discountType === 'amount'
                            ? promotion.discountAmount || 0
                            : promotion.discount || 0}
                          {promotion.discountType === 'percentage' ? '%' : ''}
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.75rem', opacity: 0.95 }}>
                          OFF
                        </Box>
                      </Typography>
                    </Box>
                  )}
                  {product.discountedPrice && !promotion && (
                    <Chip 
                      label={`${Math.round(((product.price - product.discountedPrice) / product.price) * 100)}% OFF`}
                      color="error" 
                      size="small" 
                    />
                  )}
                  {product.stockQuantity < 10 && (
                    <Chip label="Limited Stock" color="warning" size="small" />
                  )}
                </Box>
              </Box>
            </Card>

            {/* Thumbnail images and videos */}
            {mediaItems.length > 0 && (
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {mediaItems.map((item, index) => (
                  <Grid item xs={3} key={`${item.type}-${index}`}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedImageIndex === index ? 2 : 0,
                        borderColor: 'primary.main',
                        '&:hover': { boxShadow: 4 },
                        position: 'relative',
                      }}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      {item.type === 'video' ? (
                        <>
                          <Box
                            component="video"
                            src={item.url}
                            sx={{ 
                              width: '100%',
                              height: 80,
                              objectFit: 'cover',
                              aspectRatio: '1/1',
                              bgcolor: '#000',
                            }}
                            muted
                            onMouseEnter={(e) => e.target.play()}
                            onMouseLeave={(e) => {
                              e.target.pause();
                              e.target.currentTime = 0;
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlayArrow sx={{ color: 'white', fontSize: 16 }} />
                          </Box>
                        </>
                      ) : (
                        <CardMedia
                          component="img"
                          image={item.url || primaryImage}
                          alt={`${product.name} ${index + 1}`}
                          sx={{ 
                            width: '100%',
                            height: 80,
                            objectFit: 'cover',
                            aspectRatio: '1/1',
                            filter: (item.url || hasImages) ? 'none' : 'grayscale(100%)',
                            opacity: (item.url || hasImages) ? 1 : 0.7,
                          }}
                          onError={(e) => {
                            e.target.src = '/mos-logo.jpg';
                            e.target.style.filter = 'grayscale(100%)';
                            e.target.style.opacity = '0.7';
                          }}
                        />
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {/* Lot Details Section — below thumbnails, selectable cards */}
            {effectiveLots.length > 0 && (
              <Paper sx={{
                mt: 2,
                p: 2,
                bgcolor: 'secondary.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'secondary.200',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, width: 60, height: 60,
                  background: 'linear-gradient(45deg, rgba(156,39,176,0.1), rgba(233,30,99,0.1))',
                  borderRadius: '50%', transform: 'translate(-15px, -15px)'
                }} />
                <Typography variant="h6" sx={{
                  fontWeight: 700, color: 'secondary.main', mb: 1.5,
                  position: 'relative', zIndex: 1,
                  display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem'
                }}>
                  <Info sx={{ fontSize: 18 }} />
                  Lot Details
                </Typography>

                {/* One row per lot showing Length / Width / Nos (original lotNos) / Balance (remaining slabs) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, position: 'relative', zIndex: 1 }}>
                  {effectiveLots.map((lot, idx) => (
                    <Paper
                      key={effectiveLots[idx]?.lotId || idx}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderWidth: 1,
                        borderColor: 'grey.300',
                        bgcolor: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <Grid container spacing={1}>
                        {lot.lotLength != null && (
                          <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: 'grey.50', borderRadius: 1.5, boxShadow: 1, minHeight: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>Length</Typography>
                              <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: '0.95rem', my: 0.25 }}>{lot.lotLength}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>inch</Typography>
                            </Box>
                          </Grid>
                        )}
                        {lot.lotWidth != null && (
                          <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: 'grey.50', borderRadius: 1.5, boxShadow: 1, minHeight: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>Width</Typography>
                              <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: '0.95rem', my: 0.25 }}>{lot.lotWidth}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>inch</Typography>
                            </Box>
                          </Grid>
                        )}
                        {lot.lotNos != null && (
                          <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: 'grey.50', borderRadius: 1.5, boxShadow: 1, minHeight: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>Nos</Typography>
                              <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: '0.95rem', my: 0.25 }}>{lot.lotNos}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>pieces</Typography>
                            </Box>
                          </Grid>
                        )}
                        {lot.lotNos != null && (
                          <Grid item xs={3} sx={{ ml: 'auto' }}>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: 'grey.50', borderRadius: 1.5, boxShadow: 1, minHeight: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>Balance</Typography>
                              <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: '0.95rem', my: 0.25 }}>{lotBalance(lot)}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>pieces</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))}
                </Box>

                {/* Lot Location and Total SFT — shown once at the bottom */}
                <Box sx={{ mt: 1.5, position: 'relative', zIndex: 1 }}>
                  {sharedLotLocation && (
                    <Typography variant="body2" sx={{ color: '#4b5563', mb: 0.5, fontSize: '0.85rem' }}>
                      <strong>Lot Location:</strong> {sharedLotLocation}
                    </Typography>
                  )}
                  {totalSft > 0 && (
                    <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '0.85rem' }}>
                      <strong>Total SFT:</strong> {totalSft.toFixed(3)}
                    </Typography>
                  )}
                </Box>

                {/* Lot Size / Percentage info if present */}
                {(product.lotSize || product.lotPercentage) && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    {product.lotSize && <Chip label={`Lot Size: ${product.lotSize}`} size="small" color="secondary" variant="outlined" />}
                    {product.lotPercentage && <Chip label={`${product.lotPercentage}%`} size="small" color="secondary" variant="outlined" />}
                  </Box>
                )}
              </Paper>
            )}
          </motion.div>
        </Grid>

        {/* Product Information */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Product Title and Rating */}
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {product.name}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Rating 
                value={productAnalytics?.[product.id]?.averageRating || 0} 
                precision={0.1} 
                size="small" 
                readOnly
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#ffd700',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: '#e0e0e0',
                  }
                }}
              />
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                {productAnalytics?.[product.id]?.averageRating 
                  ? `(${productAnalytics[product.id].averageRating.toFixed(1)}) • ${productAnalytics[product.id].totalReviews || 0} reviews`
                  : 'No reviews yet'
                }
              </Typography>
              <Chip 
                label={product.stockQuantity > 0 ? 'In Stock' : 'Out of Stock'} 
                color={product.stockQuantity > 0 ? 'success' : 'error'}
                variant="filled"
                sx={{ ml: 'auto', fontWeight: 600 }}
              />
            </Box>

            {/* Category and Origin */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip 
                label={product.category} 
                variant="filled" 
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                label={product.origin} 
                variant="filled" 
                color="secondary"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {/* Lot Details moved below product images — see left column */}

            {/* Price */}
            <Box sx={{ 
              mb: 3,
              p: 3,
              bgcolor: 'primary.50',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'primary.200',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 100,
                height: 100,
                background: 'linear-gradient(45deg, rgba(25,118,210,0.1), rgba(66,165,245,0.1))',
                borderRadius: '50%',
                transform: 'translate(30px, -30px)'
              }} />
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
                <>
                  <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      ₹{finalUnitPrice.toFixed(2)}
                    </Box>{' '}
                    per SFT
                  </Typography>
                  {promotion && (
                    <Chip
                      label={
                        promotion.discountType === 'amount'
                          ? `Save ₹${promotion.discountAmount || 0}`
                          : `Save ${promotion.discount || 0}%`
                      }
                      color="success"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '24px'
                      }}
                    />
                  )}
                </>
              </Box>
              <Typography variant="body2" color="primary.dark" sx={{ fontWeight: 500, mt: 0.5 }}>
                +GST + Shipping Charges • Usually Ship in 3 days
              </Typography>
            </Box>

            {/* Description */}


            {/* Stock Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <CheckCircle
                sx={{
                  color: product.stockQuantity > 0 ? 'success.main' : 'error.main',
                  mr: 1
                }}
              />
              <Typography
                variant="body2"
                color={product.stockQuantity > 0 ? 'success.main' : 'error.main'}
              >
                {product.stockQuantity > 0
                  ? `In Stock`
                  : 'Out of Stock'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Max: {getMaxOrderQuantity(product)}
              </Typography>
            </Box>
            {/* Selected SFT / Available SFT */}
            {effectiveLots.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {realTimeSft > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                    Selected SFT: {realTimeSft.toFixed(2)} SFT
                  </Typography>
                )}
                {totalSft > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Available SFT: {availableSft.toFixed(2)} SFT
                  </Typography>
                )}
              </Box>
            )}

            {/* Quantity Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Nos(Quantity):
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= moqMinSlabs}
                >
                  <Remove />
                </IconButton>
                <TextField
                  value={quantity}
                  onChange={handleQuantityInputChange}
                  onBlur={handleQuantityBlur}
                  inputProps={{
                    min: moqMinSlabs,
                    max: currentProduct?.product?.stockQuantity || 999,
                    style: {
                      textAlign: 'center',
                      width: '60px',
                      padding: '8px 4px',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    },
                  }}
                  size="small"
                  variant="standard"
                  sx={{
                    '& .MuiInput-underline:before': { display: 'none' },
                    '& .MuiInput-underline:after': { display: 'none' },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { display: 'none' },
                    minWidth: 60
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= getMaxOrderQuantity(product)}
                >
                  <Add />
                </IconButton>
              </Box>
              {product.minimumOrderQuantity && parseFloat(product.minimumOrderQuantity) > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  MinOrder SFT: {parseFloat(product.minimumOrderQuantity).toFixed(2)} SFT
                </Typography>
              )}
            </Box>

            {/* Action Buttons - All Exactly Equal Size */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {/* Book a Lot - Top Priority Action */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Tooltip title="Reserve a bulk quantity of this stone for your project." arrow>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<BookOnline />}
                    onClick={handleBookALot}
                    sx={{ 
                      flex: 1, 
                      minHeight: 56,
                      minWidth: 0,
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      borderWidth: 2,
                      color: 'success.main',
                      borderColor: 'success.main',
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: 'success.dark',
                        backgroundColor: 'success.50'
                      }
                    }}
                  >
                    Book a Lot
                  </Button>
                </Tooltip>
              </Box>

              {/* Primary Actions Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCart />}
                  onClick={handleAddToCart}
                  disabled={product.stockQuantity === 0}
                  sx={{ 
                    flex: 1, 
                    minHeight: 56,
                    minWidth: 0,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Add to Cart
                </Button>
                <Tooltip title="Schedule a visit to inspect the lot before purchase" arrow>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<BookOnline />}
                    onClick={handleRequestVisit}
                    sx={{ 
                      flex: 1, 
                      minHeight: 56,
                      minWidth: 0,
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    Request Visit
                  </Button>
                </Tooltip>
              </Box>

              {/* Secondary Actions Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={isInWishlist ? <Favorite /> : <FavoriteBorder />}
                  onClick={handleWishlistToggle}
                  color={isInWishlist ? 'error' : 'primary'}
                  sx={{ 
                    flex: 1, 
                    minHeight: 56,
                    minWidth: 0,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Share />}
                  onClick={handleShare}
                  sx={{ 
                    flex: 1, 
                    minHeight: 56,
                    minWidth: 0,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  Share
                </Button>
              </Box>
            </Box>

            {/* Seller Information */}
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Seller Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Business sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {product.sellerId || 'Premium Building Materials'}
                </Typography>
                <Verified sx={{ ml: 1, color: 'primary.main', fontSize: 16 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Rating 
                  value={sellerAnalytics?.[product.sellerId]?.averageRating || 0} 
                  precision={0.1} 
                  size="small" 
                  readOnly 
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {sellerAnalytics?.[product.sellerId]?.averageRating 
                    ? `${sellerAnalytics[product.sellerId].averageRating.toFixed(1)} (${sellerAnalytics[product.sellerId].totalReviews || 0} reviews)`
                    : 'No reviews yet'
                  }
                </Typography>
              </Box>
              {/*<Button variant="outlined" size="small" startIcon={<Phone />}>
                Contact Seller
              </Button>*/}
            </Paper>
          </motion.div>
        </Grid>
      </Grid>

      {/* Product Details Tabs */}
      <Box sx={{ mt: 6 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Specifications" />
          <Tab label="Description" />
          <Tab label="Reviews" />
          <Tab label="Shipping & Returns" />
        </Tabs>

        {/* Specifications Tab */}
        {selectedTab === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper sx={{ mt: 2, p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Product Specifications
              </Typography>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '30%' }}>Category</TableCell>
                      <TableCell>{product.category}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Sub Category</TableCell>
                      <TableCell>{product.subCategory || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell>{product.material || product.specifications?.material || 'Premium Quality'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
                      <TableCell>{product.origin || product.specifications?.origin || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {product.color || product.specifications?.color ? (
                            <>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  bgcolor: (product.color || product.specifications?.color)?.toLowerCase(),
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  mr: 1
                                }}
                              />
                              {product.color || product.specifications?.color}
                            </>
                          ) : 'N/A'}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Finish</TableCell>
                      <TableCell>{product.finish || product.specifications?.finish || 'Standard'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Thickness</TableCell>
                      <TableCell>{product.thickness || product.specifications?.thickness ? `${product.thickness || product.specifications?.thickness}mm` : 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Dimensions</TableCell>
                      <TableCell>{product.dimensions || 'Standard Size'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Stock Quantity</TableCell>
                      <TableCell>
                        <Chip 
                          label={`${product.stockQuantity || 0} units`}
                          color={product.stockQuantity > 0 ? 'success' : 'error'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>

                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </motion.div>
        )}

        {/* Description Tab */}
        {selectedTab === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper sx={{ mt: 2, p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Product Description
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                {product.description}
              </Typography>

              {/* Lot Information */}
              {product.lotSize && (
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  bgcolor: 'primary.50', 
                  borderRadius: 2, 
                  border: '1px solid',
                  borderColor: 'primary.200'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    Lot Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Available Lot Size:
                    </Typography>
                    <Chip 
                      label={product.lotSize} 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                    {product.lotPercentage && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Lot Percentage:
                        </Typography>
                        <Chip 
                          label={`${product.lotPercentage}%`} 
                          color="secondary" 
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              )}
              
              {/* Features */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Key Features
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Premium quality materials" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Durable and long-lasting" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Professional grade finish" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Easy installation" />
                </ListItem>
              </List>
            </Paper>
          </motion.div>
        )}

        {/* Reviews Tab */}
        {selectedTab === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ mt: 2 }}>
              <ReviewSystem
                productId={product.id}
                showAnalytics={true}
              />
              <ReportProductButton productId={product.id} />
            </Box>
          </motion.div>
        )}

        {/* Shipping Tab */}
        {selectedTab === 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper sx={{ mt: 2, p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Shipping & Returns
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <LocalShipping color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Shipping" 
                    secondary="Usually Ship in 3 days" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Schedule color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delivery Time" 
                    secondary="3-7 business days" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Return Policy" 
                    secondary="30-day return policy for unused items" 
                  />
                </ListItem>
              </List>
            </Paper>
          </motion.div>
        )}
      </Box>

      {/* Similar Products */}
      {similarProducts && similarProducts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Similar Products
          </Typography>
          <Grid container spacing={3}>
            {similarProducts.slice(0, 4).map((similarProduct, index) => (
              <Grid item xs={12} sm={6} md={3} key={similarProduct.id || index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => navigate(`/products/${similarProduct.id}`)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={getProductPrimaryImageUrl(similarProduct, false, 'medium') || '/mos-logo.jpg'}
                      alt={similarProduct.name}
                      sx={{
                        objectFit: 'cover',
                        ...(getProductPrimaryImageUrl(similarProduct) ? {} : {
                          filter: 'grayscale(100%)',
                          opacity: 0.7,
                        }),
                      }}
                      onError={(e) => {
                        e.target.src = '/mos-logo.jpg';
                        e.target.style.filter = 'grayscale(100%)';
                        e.target.style.opacity = '0.7';
                      }}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {similarProduct.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {similarProduct.category}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ₹{((similarProduct.discountedPrice || similarProduct.price) * 1.18).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {product.name}
          <IconButton onClick={() => setImageDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {mediaItems[selectedImageIndex]?.type === 'video' ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  position: 'relative',
                  bgcolor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                }}
              >
                <Box
                  component="video"
                  src={mediaItems[selectedImageIndex].url}
                  controls
                  autoPlay
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ) : (
              <img
                src={mediaItems[selectedImageIndex]?.url || images[selectedImageIndex] || primaryImage}
                alt={product.name}
                style={{
                  filter: hasImages ? 'none' : 'grayscale(100%)',
                  opacity: hasImages ? 1 : 0.7,
                }}
                onError={(e) => {
                  e.target.src = '/mos-logo.jpg';
                  e.target.style.filter = 'grayscale(100%)';
                  e.target.style.opacity = '0.7';
                }}
                style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
              />
            )}
            {mediaItems.length > 1 && (
              <>
                <IconButton
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : mediaItems.length - 1));
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) => (prev < mediaItems.length - 1 ? prev + 1 : 0));
                  }}
                >
                  <ArrowForward />
                </IconButton>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Visit Request Dialog */}
      <VisitRequestDialog
        open={visitDialogOpen}
        onClose={() => setVisitDialogOpen(false)}
        product={product}
        onSubmit={handleVisitSubmit}
      />

      {/* Book a Lot Dialog */}
      <BookLotDialog
        open={bookLotDialogOpen}
        onClose={() => setBookLotDialogOpen(false)}
        product={currentProduct?.product}
        user={user}
      />
    </Container>
  );
};

export default ProductDetailPage;